import type { ProductExperienceDto } from '@/types';

/** DOM id of the injected Pendo agent script — used to guarantee single injection. */
const PENDO_SCRIPT_ID = 'pendo-agent';

/**
 * Injects the Pendo agent into the page and initialises it for the current session.
 *
 * Everything it needs comes from `GET /me/product-experience`: the subscription the agent
 * bundle is served under, and the visitor and account this session reports as. None of it is
 * derived here on purpose — the backend computes those identifiers with the same resolver it
 * uses for the events it sends itself, so a session's clicks and its server-side events arrive
 * as one visitor in one account. Deriving them in the browser is what produced two of each:
 * this file used to build them from the tenant UUID, which matched nothing on the server.
 *
 * Visitor and account metadata is likewise absent by design — the backend already pushes it
 * through Pendo's Metadata API, so sending a second copy from here could only drift.
 *
 * An empty `subscriptionId` means analytics is switched off for this environment: the agent is
 * not injected and the app runs untracked.
 */
export function injectPendoScript(context: ProductExperienceDto): void {
  // Already injected — Pendo only needs to be set up once per session.
  if (document.getElementById(PENDO_SCRIPT_ID)) return;

  const { subscriptionId, visitorId, accountId } = context;
  if (!subscriptionId) {
    console.warn('Pendo: no subscription configured for this environment — skipping initialization.');
    return;
  }

  try {
    const head = document.querySelector('head');
    const script = document.createElement('script');
    script.id = PENDO_SCRIPT_ID;
    script.text = `!function(e,n,t,c,i){var a,o,s,d,p;for((i=e[c]=e[c]||{})._q=i._q||[],o=0,s=(a=["initialize","identify","updateOptions","pageLoad","track"]).length;o<s;++o)!function(e){i[e]=i[e]||function(){i._q[e===a[0]?"unshift":"push"]([e].concat([].slice.call(arguments,0)))}}(a[o]);(d=n.createElement(t)).async=!0,d.src="https://content.pendo.xtm-intl.com/agent/static/${subscriptionId}/pendo.js",(p=n.getElementsByTagName(t)[0]).parentNode.insertBefore(d,p)}(window,document,"script","pendo");`;
    if (head) {
      head.appendChild(script);
    }

    pendo.initialize({
      visitor: { id: visitorId },
      account: { id: accountId },
    });
  } catch (error) {
    console.warn('Failed to inject Pendo script', error);
  }
}

/**
 * Initialises Pendo with a pre-fetched product-experience context. Safe to call repeatedly;
 * the underlying injection is idempotent.
 */
export function initializePendo(context: ProductExperienceDto): void {
  try {
    injectPendoScript(context);
  } catch (error) {
    console.warn('Failed to initialize Pendo', error);
  }
}
